import { useCallback, useEffect, useState } from 'react';
import { api, formatTarih, tokens } from '../lib/api';

/** Destek talepleri — herkes kendi açtığını, yönetici hepsini görür ve yanıtlar. */
type Talep = {
  id: number; no: string; baslik: string; aciklama: string; tur: string; oncelik: string; durum: string; bildiren: string;
  benim: boolean; olusturma: string; hedef: string | null; gecikti: boolean; yanit: string | null; yanitTarihi: string | null;
  baglam: Record<string, unknown> | null; gorseller: string[]; yeni?: boolean;
};

/** Görüntü yetki kontrollü uçtan gelir; <img> jeton taşıyamadığı için blob olarak çekilir. */
function Gorsel({ url, sira }: { url: string; sira: number }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let iptal = false, nesne: string | null = null;
    fetch(url, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} })
      .then((r) => (r.ok ? r.blob() : Promise.reject())).then((b) => { if (!iptal) { nesne = URL.createObjectURL(b); setSrc(nesne); } })
      .catch(() => undefined);
    return () => { iptal = true; if (nesne) URL.revokeObjectURL(nesne); };
  }, [url]);
  const [buyuk, setBuyuk] = useState(false);
  useEffect(() => {
    if (!buyuk) return;
    const kapat = (e: KeyboardEvent) => { if (e.key === 'Escape') setBuyuk(false); };
    window.addEventListener('keydown', kapat);
    return () => window.removeEventListener('keydown', kapat);
  }, [buyuk]);
  if (!src) return <span className="ccd-destek-kucuk ccd-destek-bekle" aria-label="Görüntü yükleniyor" />;
  // Listede küçük simge; tıklayınca tam boy açılır (ekran görüntüsü talebin kendisinden çok yer kaplamasın).
  return (
    <>
      <button type="button" className="ccd-destek-kucuk" onClick={() => setBuyuk(true)} title="Ekran görüntüsünü büyüt">
        <img src={src} alt={`Ekran görüntüsü ${sira}`} />
        <span className="ccd-destek-kucuk-ad">Görüntü {sira}</span>
      </button>
      {buyuk && (
        <div className="ccd-destek-buyuk" role="dialog" aria-modal="true" aria-label={`Ekran görüntüsü ${sira}`} onClick={() => setBuyuk(false)}>
          <img src={src} alt={`Ekran görüntüsü ${sira}`} onClick={(e) => e.stopPropagation()} />
          <div className="ccd-destek-buyuk-ust" onClick={(e) => e.stopPropagation()}>
            <a className="ccd-btn ccd-btn-sm" href={src} target="_blank" rel="noreferrer">Yeni sekmede aç</a>
            <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => setBuyuk(false)}>Kapat (Esc)</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function Destek() {
  const [veri, setVeri] = useState<{ yonetici: boolean; talepler: Talep[] } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<number | null>(null);
  const [yanit, setYanit] = useState('');
  const [suzgec, setSuzgec] = useState<'ACIK' | 'COZULDU' | 'hepsi'>('ACIK');
  const yukle = useCallback(() => {
    api<{ yonetici: boolean; talepler: Talep[] }>('/api/destek').then(setVeri).catch((e) => setHata((e as Error).message));
  }, []);
  useEffect(yukle, [yukle]);
  // Ekran açılınca taleplerime gelen yanıtlar görülmüş sayılır (yüzen düğmedeki rozet söner).
  useEffect(() => { void api('/api/destek/hepsi/goruldu', { method: 'POST' }).catch(() => undefined); }, []);

  async function yanitla(id: number, durum?: 'ACIK' | 'COZULDU') {
    setHata(null);
    try {
      await api(`/api/destek/${id}/yanit`, { method: 'POST', body: JSON.stringify({ ...(yanit.trim() ? { yanit: yanit.trim() } : {}), ...(durum ? { durum } : {}) }) });
      setYanit(''); yukle();
    } catch (e) { setHata(e instanceof Error ? e.message : 'Kaydedilemedi'); }
  }
  // Yeni yanıtı olan talep, hangi sekmede olursa olsun en üstte görünür.
  const liste = (veri?.talepler ?? []).filter((t) => suzgec === 'hepsi' || t.durum === suzgec || t.yeni)
    .sort((a, b) => Number(Boolean(b.yeni)) - Number(Boolean(a.yeni)));

  return (
    <div className="ccd-stack">
      <div>
        <h1 className="ccd-title">Destek Talepleri</h1>
        <div className="ccd-meta">Sağ alttaki <b>?</b> düğmesiyle açılan talepler · {veri?.yonetici ? 'tüm kullanıcıların talepleri' : 'yalnız sizin talepleriniz'}</div>
      </div>
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
      <div className="ccd-tabs" role="tablist">
        {([['ACIK', 'Açık'], ['COZULDU', 'Çözülen'], ['hepsi', 'Tümü']] as const).map(([id, ad]) => (
          <button key={id} role="tab" aria-selected={suzgec === id} className="ccd-tab" onClick={() => setSuzgec(id)}>
            {ad}{veri ? ` (${veri.talepler.filter((t) => id === 'hepsi' || t.durum === id).length})` : ''}</button>
        ))}
      </div>
      {!veri ? <div className="ccd-inline-note">Yükleniyor…</div> : liste.length === 0 ? (
        <div className="ccd-empty">Bu süzgeçte talep yok.</div>
      ) : liste.map((t) => (
        <div key={t.id} className="ccd-card ccd-stack-sm">
          <div className="ccd-row-between" style={{ flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => { setAcik(acik === t.id ? null : t.id); setYanit(t.yanit ?? ''); }}>
            <div>
              <b className="ccd-mono">{t.no}</b> <b>{t.baslik}</b>
              <div className="ccd-meta">{t.bildiren} · {formatTarih(t.olusturma)} · {t.tur} · {t.oncelik}{t.hedef && t.durum === 'ACIK' ? ` · hedef ${formatTarih(t.hedef)}` : ''}</div>
            </div>
            <div className="ccd-row" style={{ gap: 8 }}>
              {t.gecikti && <span className="ccd-badge ccd-badge-danger">hedef geçti</span>}
              {t.yeni && <span className="ccd-badge ccd-badge-danger">yeni yanıt</span>}
              {t.yanit && <span className="ccd-badge ccd-badge-info">yanıtlandı</span>}
              <span className={`ccd-badge${t.durum === 'COZULDU' ? ' ccd-badge-success' : ''}`}>{t.durum === 'COZULDU' ? 'çözüldü' : 'açık'}</span>
            </div>
          </div>
          {t.yanit && acik !== t.id && <div className="ccd-meta"><b>Yanıt:</b> {t.yanit}</div>}
          {acik === t.id && (
            <div className="ccd-stack-sm">
              {t.aciklama && <div style={{ whiteSpace: 'pre-wrap' }}>{t.aciklama}</div>}
              {t.baglam && (
                <div className="ccd-meta" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  Sayfa: {String(t.baglam.sayfa ?? '')} · {String(t.baglam.rota ?? '')}{'\n'}Adres: {String(t.baglam.adres ?? '')}{'\n'}
                  Zaman: {String(t.baglam.zaman ?? '')} · pencere {String(t.baglam.pencere ?? '')}{'\n'}Tarayıcı: {String(t.baglam.tarayici ?? '')}
                  {Array.isArray(t.baglam.gorunurHata) && t.baglam.gorunurHata.length > 0 && `\nEkrandaki hata: ${(t.baglam.gorunurHata as string[]).join(' | ')}`}
                  {Array.isArray(t.baglam.hatalar) && t.baglam.hatalar.length > 0 && `\nTarayıcı hata kayıtları:\n${(t.baglam.hatalar as string[]).join('\n')}`}
                </div>
              )}
              {t.gorseller.length > 0 && <div className="ccd-destek-kucukler">{t.gorseller.map((g, n) => <Gorsel key={g} url={g} sira={n + 1} />)}</div>}
              {t.yanit && <div className="ccd-banner"><span className="ccd-banner-icon">↩</span><span><b>Yanıt{t.yanitTarihi ? ` (${formatTarih(t.yanitTarihi)})` : ''}:</b> {t.yanit}</span></div>}
              {veri.yonetici && (
                <>
                  <textarea className="ccd-input" style={{ minHeight: 80 }} placeholder="Bildirene yanıt — kendi listesinde görür" value={yanit} onChange={(e) => setYanit(e.target.value)} />
                  <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <button className="ccd-btn ccd-btn-sm" onClick={() => void yanitla(t.id)}>Yanıtı kaydet</button>
                    {t.durum === 'ACIK'
                      ? <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => void yanitla(t.id, 'COZULDU')}>Yanıtla ve çözüldü işaretle</button>
                      : <button className="ccd-btn ccd-btn-sm" onClick={() => void yanitla(t.id, 'ACIK')}>Yeniden aç</button>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
